import { IntegrationSystemDisplayNameType } from 'shared/providers/IntegrationsProvider';
import {
  GetSidePanelPetParentProfileQuery,
  MergeProfileClientSearchQuery,
  PhoneNumberLookupResult,
  SidePanelParentPetDataFragment,
} from 'shared/types/graphql';

export interface MergeDisabledReason {
  code: number;
  reason: string;
}

const getMergeDisabledReason = (
  isTestClient: boolean,
  firstProfilePimsId: string | undefined,
  secondProfilePimsId: string | undefined,
  isMergeExistingPimsProfileSupported: boolean,
  pimsName: IntegrationSystemDisplayNameType | null,
  loyaltyPointBalance: number | undefined,
  hasRewardRedemptions: boolean,
  hasCarePlanEnrollment: boolean,
  multipleActiveChannels?: boolean,
): MergeDisabledReason | undefined => {
  if (isTestClient) {
    return { code: 0, reason: `This is a test client and it cannot be merged with another client.` };
  }
  if (!isMergeExistingPimsProfileSupported && !!firstProfilePimsId) {
    return { code: 1, reason: `This client is in ${pimsName} and cannot be merged with another client.` };
  }
  if (!!secondProfilePimsId && !!firstProfilePimsId) {
    return {
      code: 2,
      reason: `This client is already in ${pimsName} and cannot be merged with another ${pimsName} client.`,
    };
  }
  if (!!loyaltyPointBalance) {
    return {
      code: 3,
      reason: `This client has an existing loyalty point balance and cannot be merged with another client.`,
    };
  }
  if (hasRewardRedemptions) {
    return {
      code: 4,
      reason: `This client has existing loyalty reward redemptions and cannot be merged with another client.`,
    };
  }
  if (hasCarePlanEnrollment) {
    return {
      code: 5,
      reason: `This client has one or more pets enrolled in Care Plans and cannot be merged with another client.`,
    };
  }
  if (!!multipleActiveChannels) {
    return {
      code: 6,
      reason: `The clients you want to merge both have active Conversations. Please archive one of the Conversations before merging these profiles.`,
    };
  }

  return;
};

export const getSearchResultMergeDisabledReason = (
  profile: MergeProfileClientSearchQuery['clinicPetParentSearch'][0],
  clinicPetParentPimsId: string | undefined,
  isMergeExistingPimsProfileSupported: boolean,
  pimsName: IntegrationSystemDisplayNameType | null,
  multipleActiveChannels: boolean,
): MergeDisabledReason | undefined => {
  return getMergeDisabledReason(
    !!profile?.isTest,
    profile?.pimsId ?? undefined,
    clinicPetParentPimsId,
    isMergeExistingPimsProfileSupported,
    pimsName,
    profile?.loyaltyAccountHolder?.account.pointBalance,
    !!profile?.loyaltyAccountHolder?.account.rewardRedemptions.length,
    !!profile?.pets?.some((pet) => !!pet.organizationPet?.carePlanEnrollments.length),
    multipleActiveChannels,
  );
};

export const getSidePanelMergeDisabledReason = (
  profile: GetSidePanelPetParentProfileQuery['clinicPetParent'],
  clinicPetParentPimsId: string | undefined,
  isMergeExistingPimsProfileSupported: boolean,
  pimsName: IntegrationSystemDisplayNameType | null,
  pets: SidePanelParentPetDataFragment[],
): MergeDisabledReason | undefined => {
  return getMergeDisabledReason(
    !!profile?.isTest,
    profile?.pimsId ?? undefined,
    clinicPetParentPimsId,
    isMergeExistingPimsProfileSupported,
    pimsName,
    profile?.loyaltyAccountHolder?.account.pointBalance,
    !!profile?.loyaltyAccountHolder?.account.rewardRedemptions?.length,
    !!pets?.some((pet) => !!pet.organizationPet?.carePlanEnrollments.length),
  );
};

export const isNumberVerifiedMobile = (
  lookupResult: Pick<PhoneNumberLookupResult, 'id' | 'lineType'> | null | undefined,
): boolean => {
  return !!(lookupResult?.lineType === 'Mobile');
};
